import React, { useState } from 'react'
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import { Button, ConfirmDialog, Input, Select } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import {
  useCreateManagedDocumentCategory,
  useDeleteDocumentCategory,
  useManagedDocumentCategories,
  useRenameDocumentCategory,
} from '@/features/documents/hooks/useDocuments'
import {
  useCreateManagedExpenseCategory,
  useDeleteExpenseCategory,
  useManagedExpenseCategories,
  useRenameExpenseCategory,
} from '@/features/expenses/hooks/useExpenses'
import { cn } from '@/lib/cn'
import { FieldGroup, SettingsPane } from '../organization-ui'
import type { OrganizationSettingsFormProps } from '../settings/organizationSettingsForm.types'

type CategoryKind = 'company' | 'staff' | 'expense'

interface CategoryRowModel {
  id: string
  name: string
  usageCount: number
}

const KIND_OPTIONS: { value: CategoryKind; label: string }[] = [
  { value: 'company', label: 'Company documents' },
  { value: 'staff', label: 'Staff documents' },
  { value: 'expense', label: 'Expenses' },
]

interface CategoryListProps {
  label: string
  rows: CategoryRowModel[]
  /** Noun for the usage count, e.g. "document". */
  unit: string
  disabled: boolean
  editingId: string | null
  draftName: string
  isSaving: boolean
  onDraftChange: (value: string) => void
  onStartEdit: (row: CategoryRowModel) => void
  onCancelEdit: () => void
  onCommitEdit: (row: CategoryRowModel) => void
  onRequestDelete: (row: CategoryRowModel) => void
}

/**
 * One titled list of categories. Shared by the document and expense groups so
 * the row affordances stay identical across both.
 */
function CategoryList({
  label,
  rows,
  unit,
  disabled,
  editingId,
  draftName,
  isSaving,
  onDraftChange,
  onStartEdit,
  onCancelEdit,
  onCommitEdit,
  onRequestDelete,
}: CategoryListProps): React.ReactElement | null {
  if (rows.length === 0) return null

  return (
    <div>
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
        {label}
      </h4>
      <ul className="divide-y divide-gray-200/70 overflow-hidden rounded-xl border border-gray-200 dark:divide-gray-700/70 dark:border-gray-700">
        {rows.map((row) => {
          const isEditing = editingId === row.id
          const isLocked = row.usageCount > 0

          return (
            <li key={row.id} className="flex items-center gap-3 px-3 py-2">
              {isEditing ? (
                <Input
                  value={draftName}
                  onChange={(event) => onDraftChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      onCommitEdit(row)
                    }
                    if (event.key === 'Escape') onCancelEdit()
                  }}
                  maxLength={80}
                  autoFocus
                  aria-label={`Rename ${row.name}`}
                  className="h-9 max-w-sm"
                />
              ) : (
                <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-200">
                  {row.name}
                </span>
              )}

              <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                {row.usageCount} {row.usageCount === 1 ? unit : `${unit}s`}
              </span>

              <div className="flex shrink-0 items-center gap-0.5">
                {isEditing ? (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={!draftName.trim()}
                      loading={isSaving}
                      onClick={() => onCommitEdit(row)}
                      aria-label={`Save ${row.name}`}
                      loadingLabel={`Saving ${row.name}`}
                    >
                      {isSaving ? 'Saving…' : <Check className="h-4 w-4" strokeWidth={2} />}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={onCancelEdit}
                      aria-label="Cancel rename"
                    >
                      <X className="h-4 w-4" strokeWidth={2} />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => onStartEdit(row)}
                      aria-label={`Rename ${row.name}`}
                    >
                      <Pencil className="h-4 w-4" strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled || isLocked}
                      title={
                        isLocked
                          ? `In use by ${row.usageCount} ${row.usageCount === 1 ? unit : `${unit}s`} — empty it first`
                          : `Delete ${row.name}`
                      }
                      onClick={() => onRequestDelete(row)}
                      aria-label={`Delete ${row.name}`}
                    >
                      <Trash2
                        className={cn('h-4 w-4', !(disabled || isLocked) && 'text-danger')}
                        strokeWidth={1.75}
                      />
                    </Button>
                  </>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function CategoriesPane({
  disabled = false,
}: OrganizationSettingsFormProps): React.ReactElement {
  const documents = useManagedDocumentCategories()
  const expenses = useManagedExpenseCategories()
  const renameDocument = useRenameDocumentCategory()
  const renameExpense = useRenameExpenseCategory()
  const deleteDocument = useDeleteDocumentCategory()
  const deleteExpense = useDeleteExpenseCategory()
  const createDocument = useCreateManagedDocumentCategory()
  const createExpense = useCreateManagedExpenseCategory()

  const [newKind, setNewKind] = useState<CategoryKind>('company')
  const [newName, setNewName] = useState('')
  const [editing, setEditing] = useState<{ kind: CategoryKind; id: string } | null>(null)
  const [draftName, setDraftName] = useState('')
  const [pendingDelete, setPendingDelete] = useState<{
    kind: CategoryKind
    row: CategoryRowModel
  } | null>(null)

  const documentRows = documents.data ?? []
  const expenseRows = expenses.data ?? []
  const isLoading = documents.isLoading || expenses.isLoading
  const isCreating = createDocument.isPending || createExpense.isPending
  const isDeleting = deleteDocument.isPending || deleteExpense.isPending
  const totalCount = documentRows.length + expenseRows.length

  const addCategory = (): void => {
    const trimmed = newName.trim()
    if (!trimmed) return
    const onSuccess = (): void => setNewName('')
    if (newKind === 'expense') {
      createExpense.mutate(trimmed, { onSuccess })
    } else {
      createDocument.mutate({ ownerType: newKind, name: trimmed }, { onSuccess })
    }
  }

  const startEdit = (kind: CategoryKind) => (row: CategoryRowModel) => {
    setEditing({ kind, id: row.id })
    setDraftName(row.name)
  }

  const cancelEdit = (): void => {
    setEditing(null)
    setDraftName('')
  }

  const commitEdit = (kind: CategoryKind) => (row: CategoryRowModel) => {
    const name = draftName.trim()
    if (!name || name === row.name) {
      cancelEdit()
      return
    }
    const mutation = kind === 'expense' ? renameExpense : renameDocument
    mutation.mutate({ id: row.id, name }, { onSuccess: cancelEdit })
  }

  const confirmDelete = (): void => {
    if (!pendingDelete) return
    const { kind, row } = pendingDelete
    const mutation = kind === 'expense' ? deleteExpense : deleteDocument
    mutation.mutate(row.id, { onSuccess: () => setPendingDelete(null) })
  }

  const editingIdFor = (kind: CategoryKind): string | null =>
    editing?.kind === kind ? editing.id : null

  return (
    <SettingsPane
      title="Categories"
      description="Organization-wide categories for documents and expenses. A category can only be deleted once nothing is filed under it."
    >
      {!disabled && (
        <FieldGroup label="Add a category">
          <div className="grid gap-2 sm:grid-cols-[13rem_minmax(0,1fr)_auto]">
            <Select
              options={KIND_OPTIONS}
              value={newKind}
              onChange={(value) => setNewKind(value as CategoryKind)}
              aria-label="Category module"
            />
            <Input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  addCategory()
                }
              }}
              placeholder="Category name"
              aria-label="New category name"
              maxLength={80}
            />
            <Button
              type="button"
              onClick={addCategory}
              disabled={!newName.trim() || isCreating}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
              {isCreating ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </FieldGroup>
      )}

      <FieldGroup label="Existing categories">
        {isLoading ? (
          <SectionLoader variant="list" label="Loading categories" />
        ) : totalCount === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-4 py-8 text-center dark:border-gray-700 dark:bg-gray-900/25">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              No custom categories yet
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add one above to start organizing documents and expenses.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {(['company', 'staff'] as const).map((ownerType) => (
              <CategoryList
                key={ownerType}
                label={`${ownerType} documents`}
                rows={documentRows.filter((row) => row.ownerType === ownerType)}
                unit="document"
                disabled={disabled}
                editingId={editingIdFor(ownerType)}
                draftName={draftName}
                isSaving={renameDocument.isPending}
                onDraftChange={setDraftName}
                onStartEdit={startEdit(ownerType)}
                onCancelEdit={cancelEdit}
                onCommitEdit={commitEdit(ownerType)}
                onRequestDelete={(row) => setPendingDelete({ kind: ownerType, row })}
              />
            ))}
            <CategoryList
              label="Expenses"
              rows={expenseRows}
              unit="expense"
              disabled={disabled}
              editingId={editingIdFor('expense')}
              draftName={draftName}
              isSaving={renameExpense.isPending}
              onDraftChange={setDraftName}
              onStartEdit={startEdit('expense')}
              onCancelEdit={cancelEdit}
              onCommitEdit={commitEdit('expense')}
              onRequestDelete={(row) => setPendingDelete({ kind: 'expense', row })}
            />
          </div>
        )}
      </FieldGroup>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete category"
        message={
          pendingDelete
            ? `“${pendingDelete.row.name}” will be removed for everyone in this organization. This can't be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={isDeleting}
      />
    </SettingsPane>
  )
}

CategoriesPane.displayName = 'CategoriesPane'
